import { useEffect, useRef } from 'react';
import type { NavItem } from '@/lib/types';
import { Link } from 'react-router-dom';
import { LogOut, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import NavigationList from './NavigationList';
import styles from './MobileDrawer.module.css';

interface MobileDrawerProps {
  items: NavItem[];
  activePath: string;
  isOpen: boolean;
  onClose: () => void;
  onLogout: () => void;
}

export default function MobileDrawer({ items, activePath, isOpen, onClose, onLogout }: MobileDrawerProps) {
  const { user } = useAuth();
  const drawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    drawerRef.current?.querySelector<HTMLElement>('button, a')?.focus();

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== 'Tab' || !drawerRef.current) return;
      const focusable = Array.from(drawerRef.current.querySelectorAll<HTMLElement>('a, button:not([disabled])'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previousFocus?.focus();
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div
        className={`${styles.overlay} ${styles.overlayOpen}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        id="mobile-navigation-drawer"
        ref={drawerRef}
        className={`${styles.drawer} ${styles.drawerOpen}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div className={styles.header}>
          <span className={styles.title}>Navigation</span>
          <button className={styles.closeButton} onClick={onClose} aria-label="Close menu"><X size={18} /></button>
        </div>
        <nav aria-label="Mobile navigation">
          <NavigationList
            items={items}
            activePath={activePath}
            listClassName={styles.nav}
            linkClassName={styles.link}
            activeClassName={styles.linkActive}
            onNavigate={onClose}
          />
        </nav>
        <div className={styles.account}>
          <strong>{user?.displayName}</strong>
          <span>{user?.email}</span>
          <Link className={styles.accountAction} to="/change-password" onClick={onClose}>Change password</Link>
          <button className={styles.accountAction} type="button" onClick={onLogout}>
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </div>
    </>
  );
}
